import { Button, Form, Input, InputNumber, Row, Select, Space, Switch } from 'antd'
import { FormInstance } from 'antd/es/form/Form'
import { QuestionIcon } from 'assets/svg/questionMark'
import { trashcanSVG } from 'assets/svg/trashcan'
import { ButtonModal } from 'atom/button'
import { useState } from 'react'
import { theme } from 'theme'
import { plusSVG } from '../../../assets/svg/plus'
import { InfoTextWrapper } from '../../../atom/price'
import { EditButtonWrapper } from '../../../atom/tenants'
import { useGeneralTranslation } from '../../../hooks/translation/useGeneralTranslation'
import { usePricingSchemasTranslation } from '../../../hooks/translation/usePricingSchemaTranslation'
import { useUserTranslation } from '../../../hooks/translation/useUserTranslation'
import { PricingSchemaEventSchedule } from '../../../models/price'
import { TimeContainer } from '../../../pages/peakShaving/modal/create-new-event-modal'
import LoiteringInfoModal from './loitering-info-modal'

interface props {
  form: FormInstance
  eventSchedules: PricingSchemaEventSchedule[]
  timeOfDayLoitering: boolean | undefined
  loiteringType: string | undefined
  setPricePerUnitLoitering: (pricePerUnitLoitering: number) => void
  setTimeOfDayLoitering: (enableTimeOfDayLoiteringPricing: boolean) => void
  setLoiteringType: (loiteringType: string) => void
  hideMaxLimit?: boolean
}

export const LoiteringSectionModal: React.FC<props> = ({
  form,
  timeOfDayLoitering,
  eventSchedules,
  loiteringType,
  setPricePerUnitLoitering,
  setTimeOfDayLoitering,
  setLoiteringType,
  hideMaxLimit = false,
}) => {
  const {
    hoursNotDefinedText,
    toText,
    loiteringRateText,
    gracePeriodText,
    timeLimitText,
    timeOfDayLoiteringText,
    weekdayLoiteringRateText,
    weekendLoiteringRateText,
    maxLoiteringText,
    timeText,
    maxLoiteringInfoText,
  } = usePricingSchemasTranslation()
  const { noneText } = useUserTranslation()
  const { statusText } = useGeneralTranslation()

  const [loiteringInfoModalVisible, setLoiteringInfoModalVisible] = useState(false)

  const loiteringTypeSelector = (
    <Form.Item name="loitering_type" className="loitering-type">
      <Select
        className={loiteringType !== 'none' ? 'combine-right' : 'loitering-none'}
        style={{ borderRightWidth: '2px' }}
        onChange={(value) => {
          if (value !== 'none') {
            form.setFieldsValue({ loitering_dollar_rate: '1' })
            setPricePerUnitLoitering(1)
          } else {
            form.setFieldsValue({ loitering_dollar_rate: '0' })
            setPricePerUnitLoitering(0)
          }
          setLoiteringType(value)
        }}
        dropdownClassName="dropdown-large-font"
      >
        <Select.Option value={'none'}>{noneText}</Select.Option>
        <Select.Option value={'time_based'}>{timeText}</Select.Option>
        <Select.Option value={'status_based'}>{statusText}</Select.Option>
      </Select>
    </Form.Item>
  )

  return (
    <>
      <LoiteringInfoModal visible={loiteringInfoModalVisible} onClose={() => setLoiteringInfoModalVisible(false)} />
      <Row align="middle" style={{ marginBottom: '25px' }}>
        <Form.Item
          label={<>{loiteringRateText}:</>}
          name="loitering_dollar_rate"
          style={{ marginBottom: 0, marginRight: '10px', alignItems: 'center' }}
        >
          {loiteringType === 'none' ? (
            loiteringTypeSelector
          ) : (
            <InputNumber
              precision={2}
              controls={false}
              addonBefore={loiteringTypeSelector}
              className="combine-left"
              disabled={loiteringType === 'none' ? true : false}
              onChange={(value) => setPricePerUnitLoitering(Number(value))}
            />
          )}
        </Form.Item>
        <QuestionIcon className="pointer" onClick={() => setLoiteringInfoModalVisible(true)} />
      </Row>
      {loiteringType === 'status_based' && (
        <Form.Item
          label={<>{gracePeriodText}:</>}
          name="loitering_grace_period_minutes"
          style={{ marginBottom: '28px', alignItems: 'center' }}
        >
          <Input />
        </Form.Item>
      )}
      {loiteringType === 'time_based' && (
        <>
          <Form.Item
            label={<>{timeLimitText}:</>}
            name="loitering_time_limit_minutes"
            style={{ marginBottom: '28px', alignItems: 'center' }}
          >
            <Input />
          </Form.Item>
        </>
      )}
      {loiteringType !== 'none' && (
        <>
          {!hideMaxLimit && (
            <Form.Item
              label={<>{maxLoiteringText}:</>}
              name="loitering_maximum"
              className="loitering-maximum"
              help={maxLoiteringInfoText}
            >
              <InputNumber precision={2} controls={false} />
            </Form.Item>
          )}
          <Form.Item label={<>{timeOfDayLoiteringText}:</>}>
            <div style={{ display: 'flex' }}>
              <Switch checked={timeOfDayLoitering} onChange={(value) => setTimeOfDayLoitering(value)} />{' '}
              {timeOfDayLoitering && (
                <InfoTextWrapper>
                  {<QuestionIcon color={theme.colors.darkGray} />} {hoursNotDefinedText}
                </InfoTextWrapper>
              )}
            </div>
          </Form.Item>
          {timeOfDayLoitering && (
            <>
              <Form.List name="weekdayLoiteringRateExtras">
                {(fields, { add, remove }) => (
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <div key={key} style={{ marginTop: key === 0 ? '20px' : '0px' }}>
                          <Space key={key} style={{ marginBottom: 8 }} align="end">
                            <Form.Item
                              name={[key, 'dollar_rate']}
                              label={key === 0 ? <>{weekdayLoiteringRateText}:</> : ''}
                              style={{ marginLeft: key === 0 ? '' : '131px' }}
                            >
                              <InputNumber precision={2} controls={false} />
                            </Form.Item>
                            <TimeContainer>
                              <Form.Item name={[key, 'from']} validateTrigger={['onChange', 'onSelect']}>
                                <Select style={{ width: '141px' }} dropdownClassName="dropdown-large-font">
                                  {eventSchedules.map((eventSchedule, _) => {
                                    return (
                                      <Select.Option
                                        value={eventSchedule.from}
                                        key={
                                          eventSchedule.from < 10
                                            ? `0${eventSchedule.from}:00`
                                            : `${eventSchedule.from}:00`
                                        }
                                      >
                                        {eventSchedule.from < 10
                                          ? `0${eventSchedule.from}:00`
                                          : `${eventSchedule.from}:00`}
                                      </Select.Option>
                                    )
                                  })}
                                </Select>
                              </Form.Item>
                            </TimeContainer>
                            <div
                              style={{ height: '38px', display: 'flex', alignItems: 'flex-start', marginBottom: '5px' }}
                            >
                              {toText}
                            </div>
                            <TimeContainer>
                              <Form.Item name={[key, 'to']}>
                                <Select style={{ width: '141px' }} dropdownClassName="dropdown-large-font">
                                  {eventSchedules.map((eventSchedule, _) => {
                                    return (
                                      <Select.Option
                                        value={eventSchedule.to}
                                        key={
                                          eventSchedule.to < 10 ? `0${eventSchedule.to}:00` : `${eventSchedule.to}:00`
                                        }
                                      >
                                        {eventSchedule.to < 10 ? `0${eventSchedule.to}:00` : `${eventSchedule.to}:00`}
                                      </Select.Option>
                                    )
                                  })}
                                </Select>
                              </Form.Item>
                              {index > 0 && (
                                <ButtonModal
                                  style={{ marginLeft: '8px', marginBottom: '15px' }}
                                  type="link"
                                  icon={trashcanSVG}
                                  click={() => remove(name)}
                                />
                              )}
                            </TimeContainer>
                          </Space>
                        </div>
                      ))}
                    </div>
                    <Form.Item>
                      <EditButtonWrapper>
                        <Button
                          className="add-rate-button"
                          type="primary"
                          shape="circle"
                          icon={plusSVG}
                          onClick={add}
                          loading={false}
                          disabled={false}
                        />
                      </EditButtonWrapper>
                    </Form.Item>
                  </div>
                )}
              </Form.List>
              <Form.List name="weekendLoiteringRateExtras">
                {(fields, { add, remove }) => (
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <Space key={key} style={{ display: 'flex', marginBottom: 8, marginTop: 0 }} align="end">
                          <Form.Item
                            name={[key, 'dollar_rate']}
                            label={key === 0 ? <>{weekendLoiteringRateText}:</> : ''}
                            style={{ marginLeft: key === 0 ? '' : '131px' }}
                          >
                            <InputNumber precision={2} controls={false} />
                          </Form.Item>
                          <TimeContainer>
                            <Form.Item name={[key, 'from']} validateTrigger={['onChange', 'onSelect']}>
                              <Select style={{ width: '141px' }} dropdownClassName="dropdown-large-font">
                                {eventSchedules.map((eventSchedule, _) => {
                                  return (
                                    <Select.Option
                                      value={eventSchedule.from}
                                      key={
                                        eventSchedule.from < 10
                                          ? `0${eventSchedule.from}:00`
                                          : `${eventSchedule.from}:00`
                                      }
                                    >
                                      {eventSchedule.from < 10
                                        ? `0${eventSchedule.from}:00`
                                        : `${eventSchedule.from}:00`}
                                    </Select.Option>
                                  )
                                })}
                              </Select>
                            </Form.Item>
                          </TimeContainer>
                          <div
                            style={{ height: '38px', display: 'flex', alignItems: 'flex-start', marginBottom: '5px' }}
                          >
                            {toText}
                          </div>
                          <TimeContainer>
                            <Form.Item name={[key, 'to']}>
                              <Select style={{ width: '141px' }} dropdownClassName="dropdown-large-font">
                                {eventSchedules.map((eventSchedule, _) => {
                                  return (
                                    <Select.Option
                                      value={eventSchedule.to}
                                      key={eventSchedule.to < 10 ? `0${eventSchedule.to}:00` : `${eventSchedule.to}:00`}
                                    >
                                      {eventSchedule.to < 10 ? `0${eventSchedule.to}:00` : `${eventSchedule.to}:00`}
                                    </Select.Option>
                                  )
                                })}
                              </Select>
                            </Form.Item>
                            {index > 0 && (
                              <ButtonModal
                                style={{ marginLeft: '8px', marginBottom: '15px' }}
                                type="link"
                                icon={trashcanSVG}
                                click={() => remove(name)}
                              />
                            )}
                          </TimeContainer>
                        </Space>
                      ))}
                    </div>
                    <Form.Item>
                      <EditButtonWrapper>
                        <Button
                          className="add-rate-button"
                          type="primary"
                          shape="circle"
                          icon={plusSVG}
                          onClick={add}
                          loading={false}
                          disabled={false}
                        />
                      </EditButtonWrapper>
                    </Form.Item>
                  </div>
                )}
              </Form.List>
            </>
          )}
        </>
      )}
    </>
  )
}
